import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './roles.css';
import { ROUTES } from '../../../constants/routes';
import {
  usePermissionsQuery,
  useRoleQuery,
  useUpdateRoleMutation,
} from '../../../hooks/useDashboardQueries';
import { ErrorMessage } from '../../../components/common/ErrorMessage/ErrorMessage';
import { RoleForm } from './RoleForm';
import { MobileBottomNav } from '../Stations/MobileBottomNav';

export const EditRole = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { roleId } = useParams<{ roleId: string }>();
  const [formError, setFormError] = useState<string | undefined>();

  const groupsQ = usePermissionsQuery();
  const roleQ = useRoleQuery(roleId);
  const updateMutation = useUpdateRoleMutation(roleId ?? '');

  const handleBack = () =>
    navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_ROLES}`);

  const loading = groupsQ.isLoading || roleQ.isLoading;
  const error = groupsQ.error ?? roleQ.error;

  const handleSubmit = async (payload: {
    name: string;
    permissionIds: string[];
  }) => {
    setFormError(undefined);
    try {
      const updatePayload = roleQ.data?.isBuiltIn
        ? { permissionIds: payload.permissionIds }
        : payload;
      await updateMutation.mutateAsync(updatePayload);
      navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_ROLES}`);
    } catch (e) {
      const msg =
        (e as { response?: { data?: { message?: string } }; message?: string })
          ?.response?.data?.message ?? (e as Error).message;
      setFormError(msg);
    }
  };

  const body = (
    <>
      {error ? (
        <ErrorMessage
          message={(error as Error).message}
          onRetry={() => {
            groupsQ.refetch();
            roleQ.refetch();
          }}
        />
      ) : loading || !groupsQ.data || !roleQ.data ? (
        <p>{t('dashboard.roles.loadingPermissions')}</p>
      ) : (
        <RoleForm
          initial={roleQ.data}
          permissionGroups={groupsQ.data}
          onCancel={handleBack}
          isSubmitting={updateMutation.isPending}
          errorMessage={formError}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );

  const title = roleQ.data
    ? t('dashboard.roles.editTitle', { name: roleQ.data.name })
    : t('dashboard.roles.editTitleFallback');

  return (
    <>
      {/* Desktop */}
      <div className="desktop-layout">
        <div className="content-inner" style={{ padding: '2.5rem' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <button type="button" className="back-link" onClick={handleBack}>
              <span className="material-symbols-outlined">arrow_back</span>
              {t('dashboard.roles.backToList')}
            </button>
            <h2 style={{ marginTop: '1rem' }}>{title}</h2>
          </div>
          {body}
        </div>
      </div>

      {/* Mobile */}
      <div className="mobile-layout">
        <header className="mobile-header">
          <div className="mobile-header-top">
            <button
              type="button"
              className="mobile-icon-btn"
              onClick={handleBack}
              aria-label={t('dashboard.roles.backToList')}
            >
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
            <h1 className="mobile-header-title">{title}</h1>
            <span style={{ width: '2.5rem' }} aria-hidden="true" />
          </div>
        </header>

        <main
          className="mobile-content"
          style={{ padding: '1rem 1rem 6.5rem' }}
        >
          {body}
        </main>

        <MobileBottomNav />
      </div>
    </>
  );
};

export default EditRole;
